import { useEffect, useRef } from 'react';
import { apiService } from 'services/api';
import { CONFIG } from 'config';
import { getErrorMessage } from 'utils/errors';
import { parseEirText, normalizeExportedNotes, EIR_FILE_STORAGE_KEY } from 'utils/appHelpers';
import { persistence } from 'services/persistence';
import { useAsyncEffect } from 'hooks/useAsyncEffectGuard';
import type { RemoteEhrSession } from 'types';
import type { EIRData } from 'types';
import type { AppAction } from 'context/appReducerTypes';

export interface UseRemoteEhrPollingParams {
  remoteEhrSession: RemoteEhrSession | null;
  setRemoteEhrSession: React.Dispatch<React.SetStateAction<RemoteEhrSession | null>>;
  isMobileDevice: boolean;
  dispatch: React.Dispatch<AppAction>;
  setRemoteEhrError: (error: string | null) => void;
  setIsBankIdModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFetchingRemoteEhrData: (fetching: boolean) => void;
  setStoredTxtContent: (content: string) => void;
  setShowEirLoadOptionsModal: (show: boolean) => void;
  remoteEhrFetchedRef: React.MutableRefObject<boolean>;
  greetingSentRef: React.MutableRefObject<string | null>;
}

/**
 * Handles remote EHR session polling: mobile token, status, and data fetching when completed.
 */
export function useRemoteEhrPolling({
  remoteEhrSession,
  setRemoteEhrSession,
  isMobileDevice,
  dispatch,
  setRemoteEhrError,
  setIsBankIdModalOpen,
  setIsFetchingRemoteEhrData,
  setStoredTxtContent,
  setShowEirLoadOptionsModal,
  remoteEhrFetchedRef,
  greetingSentRef,
}: UseRemoteEhrPollingParams): void {
  const remoteEhrSessionRef = useRef<RemoteEhrSession | null>(null);

  useEffect(() => {
    remoteEhrSessionRef.current = remoteEhrSession;
  }, [remoteEhrSession]);

  // Mobile autostart token polling
  useAsyncEffect(async (isCancelled) => {
    if (!remoteEhrSession || !isMobileDevice) return;

    const currentSessionId = remoteEhrSession.sessionId;
    const currentStatus = remoteEhrSession.status;

    if (currentStatus !== 'awaiting_bankid' && currentStatus !== 'initializing') return;

    let deepLinkOpened = false;
    let intervalId: number | null = null;

    const pollMobileToken = async () => {
      if (isCancelled() || deepLinkOpened) return;

      try {
        const tokenData = await apiService.getMobileAutostartToken(currentSessionId);
        if (isCancelled() || deepLinkOpened) return;

        if (tokenData) {
          deepLinkOpened = true;
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          const deepLink = `bankid:///?autostarttoken=${tokenData.autoStartToken}`;
          window.location.href = deepLink;
          setTimeout(() => {
            const link = document.createElement('a');
            link.href = deepLink;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }, 100);
        }
      } catch (error) {
        if (error instanceof Error && error.message.includes('404')) return;
        console.warn('[Mobile BankID] Error polling for token:', error);
      }
    };

    await pollMobileToken();
    if (isCancelled()) return;
    intervalId = window.setInterval(pollMobileToken, 1000);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [remoteEhrSession?.sessionId, remoteEhrSession?.status, isMobileDevice]);

  // Status polling
  useAsyncEffect(async (isCancelled) => {
    if (!remoteEhrSession) return;

    const currentSessionId = remoteEhrSession.sessionId;
    const currentStatus = remoteEhrSession.status;

    if (['completed', 'failed', 'cancelled'].includes(currentStatus)) return;

    const intervalRef = { current: null as number | null };

    const pollStatus = async () => {
      if (isCancelled()) return;

      try {
        const status = await apiService.getRemoteEhrStatus(currentSessionId);
        if (isCancelled()) return;

        const isTerminal = ['completed', 'failed', 'cancelled'].includes(status.status);
        if (isTerminal && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        setRemoteEhrSession((previous) => {
          const statusChanged = previous?.status !== status.status;
          const qrChanged = (status.qrImage ?? null) !== previous?.qrImage;
          const errorChanged = (status.error ?? null) !== previous?.error;

          if (!statusChanged && !qrChanged && !errorChanged) return previous;

          const newSession = {
            sessionId: status.sessionId,
            status: status.status,
            qrImage: status.qrImage ?? previous?.qrImage ?? null,
            error: status.error ?? null,
            createdAt: status.createdAt ?? previous?.createdAt,
            updatedAt: status.updatedAt ?? Date.now(),
          };

          if (statusChanged && status.status === 'completed' && !isMobileDevice) {
            setTimeout(() => {
              setIsBankIdModalOpen((current) => (!current ? true : current));
            }, 300);
          }

          return newSession;
        });

        if (status.status === 'failed' && status.error) {
          setRemoteEhrError(status.error);
          dispatch({ type: 'SET_ERROR', payload: status.error });
          window.dispatchEvent(new CustomEvent('eir:bankid-error', { detail: { error: status.error } }));
        } else if (status.status === 'cancelled') {
          window.dispatchEvent(new CustomEvent('eir:bankid-cancel'));
        } else if (status.status === 'awaiting_bankid') {
          setRemoteEhrError(null);
        }
      } catch (error) {
        if (!isCancelled()) {
          setRemoteEhrError(getErrorMessage(error, 'Unable to update BankID session.'));
        }
      }
    };

    await pollStatus();
    if (isCancelled()) return;
    intervalRef.current = window.setInterval(pollStatus, CONFIG.EHR.POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [remoteEhrSession?.sessionId, remoteEhrSession?.status, isMobileDevice, dispatch, setRemoteEhrSession, setRemoteEhrError, setIsBankIdModalOpen]);

  // Data fetching when completed
  useAsyncEffect(async (isCancelled) => {
    if (!remoteEhrSession || remoteEhrSession.status !== 'completed') return;
    if (remoteEhrFetchedRef.current) return;

    remoteEhrFetchedRef.current = true;
    setRemoteEhrError(null);
    setIsFetchingRemoteEhrData(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      if (isCancelled()) return;

      let parsedData: EIRData | null = null;
      let eirContentToStore: string | null = null;
      try {
        const eirYaml = await apiService.getRemoteEhrEirYaml(remoteEhrSession.sessionId);
        if (isCancelled()) return;
        eirContentToStore = eirYaml;
        const { data } = parseEirText(eirYaml);
        parsedData = data;
      } catch (yamlErr) {
        if (isCancelled()) return;
        const jsonData = await apiService.getRemoteEhrData(remoteEhrSession.sessionId);
        if (isCancelled()) return;
        parsedData = jsonData;
      }
      if (isCancelled()) return;

      if (eirContentToStore) {
        persistence.setItem(EIR_FILE_STORAGE_KEY, eirContentToStore);
      }

      dispatch({ type: 'RESET_HEALTH_ANALYSIS' });
      dispatch({ type: 'SET_DATA', payload: parsedData });
      dispatch({ type: 'SET_JOURNAL_NOTES', payload: normalizeExportedNotes(parsedData?.journal_entries) });
      dispatch({ type: 'SET_ACTIVE_PAGE', payload: 'ehr' });
      dispatch({ type: 'SET_ERROR', payload: null });
      setShowEirLoadOptionsModal((parsedData?.entries?.length || 0) > 0);
      greetingSentRef.current = null;

      try {
        if (isCancelled()) return;
        const txtResponse = await fetch(
          `${CONFIG.API_BASE_URL}/api/ehr/session/${remoteEhrSession.sessionId}/export/txt`
        );
        if (isCancelled()) return;
        if (txtResponse.ok) {
          const txtContent = await txtResponse.text();
          if (isCancelled()) return;
          setStoredTxtContent(txtContent);
        }
      } catch {
        // Ignore TXT fetch errors
      }

      window.dispatchEvent(
        new CustomEvent('eir:bankid-complete', {
          detail: { entriesCount: parsedData?.entries?.length || 0 },
        })
      );
    } catch (error) {
      console.error('[BankID] Error loading journal data:', error);
      if (!isCancelled()) {
        const message = getErrorMessage(error, 'Unable to import journal data.');
        setRemoteEhrError(message);
        dispatch({ type: 'SET_ERROR', payload: message });
        window.dispatchEvent(new CustomEvent('eir:bankid-error', { detail: { error: message } }));
      }
    } finally {
      if (!isCancelled()) {
        setIsFetchingRemoteEhrData(false);
      }
    }
  }, [remoteEhrSession, dispatch, remoteEhrFetchedRef, greetingSentRef, setRemoteEhrError, setIsFetchingRemoteEhrData, setStoredTxtContent, setShowEirLoadOptionsModal]);
}
